/**
 * Cart page custom behaviours (Telmont FR)
 *
 * Since the downgrade from Shopify Plus to Shopify Advanced the checkout can no
 * longer host UI extensions, so the following features live on the cart page:
 *  - Gift message (cart attributes `gift_message` + `include_gift_message`, same keys as the
 *    former checkout extension). The message is saved through the Ajax API only: JSON keeps
 *    the literal "\r\n" text required by the logistics partner for every line break (real
 *    CR+LF characters are normalised to LF by Shopify's checkout). The sequence counts in the max length.
 *  - Terms & conditions / legal age acceptance gating the "Procéder au paiement" button
 *  - Personalisation preview scaling (unchanged)
 *
 * The cart form is submitted natively (POST /cart with name="checkout") so every
 * hidden attribute input (abv, accise_tax, vignette_tax, gift message, terms…) and
 * the free-shipping discount input reach the checkout exactly as before.
 */
(function () {
  'use strict';

  var GIFT_STORAGE_KEY = 'gift_message';
  var FORBIDDEN_CHARS = ['"', '_', '=', '+', '°', '~', '{', '}', '@', '^', '`', '<', '>', ':', ';', '/', '§', '¨', '£', '¤', '%', 'µ', '|', '[', ']', '&', '#'];
  // Emojis / pictograms are stripped on input.
  var EMOJI_REGEX = /(?:[✀-➿]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[#-9]️?⃣|㊙|㊗|〽|〰|Ⓜ|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|🆎|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|🈚|🈯|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|‼|⁉|[▪-▫]|▶|◀|[◻-◾]|©|®|™|ℹ|🀄|[☀-⛿]|⬅|⬆|⬇|⬛|⬜|⭐|⭕|⌚|⌛|⌨|⏏|[⏩-⏳]|[⏸-⏺]|🃏|⤴|⤵|[←-⇿])/g;

  // Logistics (Veolog) prints the card from the attribute and expects the literal text "\r\n"
  // (backslash r, backslash n: 4 characters) for every line break. Real CR+LF characters are not
  // an option: Shopify's checkout normalises them to LF. The sequence counts in the max length.
  var LITERAL_CRLF = '\\r\\n';
  function toCRLF(value) {
    return String(value || '').replace(/\r?\n/g, LITERAL_CRLF);
  }
  // Back to real line breaks for the textarea (the attribute is rendered into it by Liquid).
  function fromCRLF(value) {
    return String(value || '').replace(/\\r\\n/g, '\n');
  }

  function cartRoute(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/' + path;
  }

  function saveAttributes(attributes, keepalive) {
    return fetch(cartRoute('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: !!keepalive,
    }).catch(function () {});
  }

  // Resubmits the cart form once the gift message is saved (second pass of the submit guard).
  function resubmit(form, submitter) {
    var button = submitter && submitter.form === form ? submitter : null;
    if (typeof form.requestSubmit === 'function') {
      try {
        button ? form.requestSubmit(button) : form.requestSubmit();
        return;
      } catch (err) {}
    }
    if (button && button.name) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = button.name;
      hidden.value = button.value || '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  function safeStorage(action, value) {
    try {
      if (action === 'get') return window.localStorage.getItem(GIFT_STORAGE_KEY);
      if (action === 'set') return window.localStorage.setItem(GIFT_STORAGE_KEY, value);
      if (action === 'remove') return window.localStorage.removeItem(GIFT_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Gift message                                                        */
  /* ------------------------------------------------------------------ */
  function initGiftMessage() {
    var root = document.querySelector('[data-gift-message]');
    if (!root) return null;

    var checkbox = root.querySelector('#gift_checkbox');
    var content = root.querySelector('.gift-message-content');
    var textarea = root.querySelector('[data-gift-message-input]');
    var giftKey = root.getAttribute('data-gift-attribute') || 'gift_message';
    var flagKey = root.getAttribute('data-gift-flag-attribute') || 'include_gift_message';
    var saveTimer = null;
    var cta = root.querySelector('#gift-validation-cta');
    var errorsEl = root.querySelector('#errors');
    var countEl = root.querySelector('#count');
    var messagesBlock = root.querySelector('.validation-messages');
    var errorMsg = root.querySelector('.validation-messages p.error');
    var successMsg = root.querySelector('.validation-messages p.success');
    var flagInput = root.querySelector('[data-gift-flag-attribute]');

    var maxLines = parseInt(root.getAttribute('data-gift-max-lines'), 10) || 5;
    var charsPerLine = parseInt(root.getAttribute('data-gift-chars-per-line'), 10) || 70;
    var maxChars = maxLines * charsPerLine;

    var textOutput = '';
    var onPaste = false;

    function isOpen() {
      return checkbox.checked;
    }

    function attributesPayload() {
      var attrs = {};
      attrs[giftKey] = isOpen() ? toCRLF(textarea.value) : '';
      attrs[flagKey] = isOpen() ? 'true' : 'false';
      return attrs;
    }

    // Saves the message (CR+LF line breaks) and the gift flag as cart attributes.
    function persist(keepalive) {
      clearTimeout(saveTimer);
      return saveAttributes(attributesPayload(), keepalive);
    }

    // Debounced save while typing, so a reload or a cart re-render keeps the message.
    function syncValue() {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(function () {
        persist(false);
      }, 400);
    }

    function setOpen(open, persistNow) {
      content.style.display = open ? 'block' : 'none';
      if (flagInput) flagInput.value = open ? 'true' : 'false';
      if (!open) {
        textarea.value = '';
        safeStorage('remove');
        updateCount();
      }
      if (persistNow) persist(false);
    }

    // The counter shows the length as it will be sent (CR+LF counts for 2 characters).
    function updateCount() {
      if (countEl) countEl.textContent = toCRLF(textarea.value).length + '/' + maxChars + ' caractères autorisés';
    }

    function flash(el, text, large) {
      if (!el) return;
      el.textContent = text;
      if (large) el.classList.add('large-message');
      el.classList.add('visible');
      if (messagesBlock) messagesBlock.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      setTimeout(function () {
        el.classList.remove('visible');
        el.classList.remove('large-message');
      }, 2000);
    }

    // Reflow the text so that no line exceeds `charsPerLine` and no more than `maxLines` lines are used.
    function formatTextAsRequired() {
      var textInput = textarea.value;
      var inputAsRows = textInput.split('\n');
      var inputAsOneLine = textInput.replace(/(\r\n\t|\n|\r\t)/gm, '');
      var cursorPositionOnInput = textarea.selectionStart;
      var cursorOffsetAfterOutput = 0;
      var visibleCharactersPerLine = Math.floor(textarea.offsetWidth / 9) || charsPerLine;
      var additionalTextAreaRows = 0;
      var totalRows = inputAsRows.length;
      var row;

      for (row = 0; row < totalRows; ++row) {
        if (inputAsRows[row].length > charsPerLine) {
          if (inputAsRows[row + 1] === undefined) {
            inputAsRows[row + 1] = '';
            totalRows++;
          }
          inputAsRows[row + 1] = inputAsRows[row].substring(charsPerLine) + inputAsRows[row + 1];
          inputAsRows[row] = inputAsRows[row].substring(0, charsPerLine);
          var newOutput = inputAsRows.join('\n');
          if (newOutput.substr(cursorPositionOnInput - 1, 1) === '\n') {
            cursorOffsetAfterOutput = 1;
          }
        }
        if (inputAsRows[row].length > visibleCharactersPerLine) {
          additionalTextAreaRows += Math.floor(inputAsRows[row].length / visibleCharactersPerLine);
        }
      }

      if (inputAsRows.length <= maxLines && toCRLF(inputAsRows.join('\n')).length <= maxChars) {
        textOutput = inputAsRows.join('\n');
        textarea.rows = inputAsRows.length + additionalTextAreaRows;
        if (errorsEl) errorsEl.textContent = '';
        if (countEl) countEl.textContent = toCRLF(textOutput).length + '/' + maxChars + ' caractères autorisés';
        if (onPaste) {
          cursorOffsetAfterOutput = cursorOffsetOnPaste(textInput, cursorPositionOnInput, totalRows);
        }
      } else {
        if (errorsEl) errorsEl.textContent = 'Vous avez dépassé la limite autorisée de ' + maxChars + ' caractères';
        if (countEl) countEl.textContent = '';
        cursorOffsetAfterOutput = -1;
      }

      textarea.value = textOutput;
      textarea.selectionStart = cursorPositionOnInput + cursorOffsetAfterOutput;
      textarea.selectionEnd = cursorPositionOnInput + cursorOffsetAfterOutput;
      onPaste = false;
      syncValue();
    }

    function countLineBreaks(string, lengthFromStart) {
      return string.substr(0, lengthFromStart).split('\n').length;
    }

    function cursorOffsetOnPaste(textInput, cursorPositionOnInput, totalRows) {
      var countOld = countLineBreaks(textInput, cursorPositionOnInput);
      var countNew = countLineBreaks(textOutput, cursorPositionOnInput + totalRows);
      return countNew - countOld;
    }

    // Returns the list of forbidden characters found in the message (empty when valid).
    function findForbiddenChars(str) {
      var found = [];
      for (var i = 0; i < str.length; i++) {
        var c = str.charAt(i);
        if (FORBIDDEN_CHARS.indexOf(c) !== -1 && found.indexOf(c) === -1) found.push(c);
      }
      return found;
    }

    function validate(showSuccess) {
      if (!isOpen()) return true;
      var value = textarea.value;
      var forbidden = findForbiddenChars(value);
      if (forbidden.length > 0) {
        var message = 'Votre message contient un ou plusieurs caractères non autorisés [' + forbidden.join(', ') + ']';
        flash(errorMsg, message, message.length > 79);
        return false;
      }
      if (toCRLF(value).length > maxChars) {
        flash(errorMsg, 'Vous avez dépassé la limite autorisée de ' + maxChars + ' caractères', true);
        return false;
      }
      syncValue();
      safeStorage('set', value);
      if (showSuccess) flash(successMsg, 'Votre message a bien été enregistré');
      return true;
    }

    // Restore a message saved locally (page reload before checkout) when the cart has none yet.
    var saved = safeStorage('get');
    if (saved && !textarea.value) {
      textarea.value = saved;
      checkbox.checked = true;
    }
    textarea.value = fromCRLF(textarea.value);
    if (isOpen()) {
      textarea.value = textarea.value.trim();
    }
    setOpen(isOpen(), false);
    updateCount();

    checkbox.addEventListener('change', function () {
      setOpen(isOpen(), true);
    });

    textarea.addEventListener('input', function () {
      this.value = this.value.replace(EMOJI_REGEX, '');
      formatTextAsRequired();
    });
    textarea.addEventListener('paste', function () {
      onPaste = true;
    });
    textarea.addEventListener('focus', function () {
      this.classList.remove('inactive');
      this.classList.add('active');
    });
    textarea.addEventListener('blur', function () {
      this.classList.remove('active');
      this.classList.add('inactive');
    });

    if (cta) {
      cta.addEventListener('click', function (e) {
        e.preventDefault();
        validate(true);
      });
    }

    return {
      validate: function () {
        return validate(false);
      },
      // Make sure no stale message is sent when the gift option is unchecked.
      beforeSubmit: function () {
        if (!isOpen()) {
          textarea.value = '';
          safeStorage('remove');
        }
      },
      // Saves the message right before the form is submitted (keepalive survives the navigation).
      persist: function () {
        return persist(true);
      },
      clearStorage: function () {
        safeStorage('remove');
      },
    };
  }

  /* ------------------------------------------------------------------ */
  /* Terms & conditions gate                                             */
  /* ------------------------------------------------------------------ */
  function initTermsGate(gift) {
    var form = document.getElementById('cartFormSubmit');
    var checkbox = document.querySelector('[data-cart-terms-checkbox]');
    var attributeInput = document.querySelector('[data-cart-terms-attribute]');
    var errorEl = document.querySelector('[data-cart-terms-error]');
    var wrapper = document.querySelector('.cart_checkbox');
    if (!form || !checkbox) return;

    function submitButton() {
      return document.getElementById('cartSubmit');
    }

    function accepted() {
      return checkbox.checked;
    }

    function sync() {
      var btn = submitButton();
      if (btn) btn.disabled = !accepted();
      if (attributeInput) attributeInput.value = accepted() ? 'true' : '';
      if (accepted()) {
        if (errorEl) errorEl.classList.remove('is-visible');
        if (wrapper) wrapper.classList.remove('cart_checkbox--error');
      }
    }

    function showError() {
      if (errorEl) errorEl.classList.add('is-visible');
      if (wrapper) wrapper.classList.add('cart_checkbox--error');
      if (wrapper) wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    checkbox.addEventListener('change', sync);
    sync();

    // Keep the button state after the theme re-renders the cart (morphdom on quantity change).
    var cartSection = document.querySelector('.cart-section-js');
    if (cartSection && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        var btn = submitButton();
        if (btn && btn.disabled === accepted()) sync();
      });
      observer.observe(cartSection, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    }

    // Guard the native submission: terms must be accepted and the gift message must be valid.
    form.addEventListener(
      'submit',
      function (e) {
        var submitter = e.submitter || document.activeElement;
        var isCheckout = !submitter || submitter.id === 'cartSubmit' || submitter.name === 'checkout';
        if (!isCheckout) return;

        // Second pass: the gift message is saved, let the theme handlers and the browser proceed.
        if (form.dataset.giftSynced === '1') {
          delete form.dataset.giftSynced;
          if (attributeInput) attributeInput.value = 'true';
          return;
        }

        if (!accepted()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showError();
          return false;
        }
        if (attributeInput) attributeInput.value = 'true';
        if (gift) {
          gift.beforeSubmit();
          if (!gift.validate()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          }
          // The message goes through the Ajax API (keeps CR+LF): save it first, then resubmit.
          e.preventDefault();
          e.stopImmediatePropagation();
          gift.persist().then(
            function () {
              gift.clearStorage();
            },
            function () {}
          ).then(function () {
            form.dataset.giftSynced = '1';
            resubmit(form, submitter);
          });
          return false;
        }
      },
      true
    );

    // Clicking the disabled button does not fire, so surface the error when the wrapper is clicked.
    var btnGroup = document.querySelector('.cart-footer .btn-group');
    if (btnGroup) {
      btnGroup.addEventListener('click', function () {
        if (!accepted()) showError();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Personalisation preview (unchanged)                                 */
  /* ------------------------------------------------------------------ */
  function initPersonalizePreview() {
    var previewValueWrap = document.querySelectorAll('.js-cart__mini-personalize-preview');
    var previewValueHidden = document.querySelectorAll('.js-cart__mini-personalize-value-hidden');
    previewValueHidden.forEach(function (preview, index) {
      if (!previewValueWrap[index]) return;
      var valueWidth = preview.getBoundingClientRect().width;
      var valueWrapWidth = previewValueWrap[index].getBoundingClientRect().width;
      if (valueWidth >= valueWrapWidth) {
        previewValueWrap[index].setAttribute('style', '--personalize-text-scale: ' + valueWrapWidth / valueWidth);
      } else {
        previewValueWrap[index].setAttribute('style', '--personalize-text-scale: 1');
      }
    });
  }

  function init() {
    var gift = initGiftMessage();
    initTermsGate(gift);
    initPersonalizePreview();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
